<template>
  <div
    v-if="article.license"
    class="license-information border-bottom border-thick border-inverted mt-3 pb-3"
  >
    <h2 class="mb-2 ml-2 font-weight-bold font-smaller">License</h2>
    <div v-if="showLabel" class="mb-2 ml-2 font-smaller">{{ label }}</div>
    <div v-else-if="!newArticleDetailsDisplay" class="mb-2 ml-2 font-smaller">
      <img
        v-if="showCC"
        class="license-information__image"
        alt="CC logo"
        src="~/static/images/license/cc.svg"
      />
      <img
        v-if="showBY"
        class="license-information__image"
        alt="BY logo"
        src="~/static/images/license/by.svg"
      />
      <img
        v-if="showNC"
        class="license-information__image"
        alt="NC logo"
        src="~/static/images/license/nc.svg"
      />
      <img
        v-if="showSA"
        class="license-information__image"
        alt="SA logo"
        src="~/static/images/license/sa.svg"
      />
      <img
        v-if="showND"
        class="license-information__image"
        alt="ND logo"
        src="~/static/images/license/nd.svg"
      />
      <img
        v-if="showZERO"
        class="license-information__image"
        alt="ZERO logo"
        src="~/static/images/license/zero.svg"
      />
    </div>

    <div v-else class="license-icon mb-2 ml-2 font-smaller d-flex flex-wrap">
      <div
        v-if="showLicenseLabel('CC')"
        class="license-icon__container text-center"
      >
        <img
          class="license-icon__image"
          alt="CC logo"
          src="~/static/images/license/cc.svg"
        />
        <div class="license-icon__label">CC</div>
      </div>
      <div
        v-if="showLicenseLabel('BY')"
        class="license-icon__container text-center"
      >
        <img
          class="license-icon__image"
          alt="BY logo"
          src="~/static/images/license/by.svg"
        />
        <div class="license-icon__label">BY</div>
      </div>
      <div
        v-if="showLicenseLabel('NC')"
        class="license-icon__container text-center"
      >
        <img
          class="license-icon__image"
          alt="NC logo"
          src="~/static/images/license/nc.svg"
        />
        <div class="license-icon__label">NC</div>
      </div>
      <div
        v-if="showLicenseLabel('SA')"
        class="license-icon__container text-center"
      >
        <img
          class="license-icon__image"
          alt="SA logo"
          src="~/static/images/license/sa.svg"
        />
        <div class="license-icon__label">SA</div>
      </div>
      <div
        v-if="showLicenseLabel('ND')"
        class="license-icon__container text-center"
      >
        <img
          class="license-icon__image"
          alt="ND logo"
          src="~/static/images/license/nd.svg"
        />
        <div class="license-icon__label">ND</div>
      </div>
      <div
        v-if="showLicenseLabel('CC 0')"
        class="license-icon__container text-center"
      >
        <img
          class="license-icon__image"
          alt="ZERO logo"
          src="~/static/images/license/zero.svg"
        />
        <div class="license-icon__label">Public Domain</div>
      </div>
    </div>

    <div class="pl-2 font-small">
      <div v-if="!showLabel" class="mt-2">
        <span v-if="hasLink && !newArticleDetailsDisplay">
          {{ copyrightWithLink }}
          <a :href="copyrightLink" target="_blank" data-test-id="LicenseLink">
            <span>CreativeCommons.org</span>
            <span class="d-sr-only">[opens in a new tab]</span>
            <v-icon small color="primary" aria-hidden="true">{{
              mdiOpenInNew
            }}</v-icon>
          </a>
        </span>

        <span v-else-if="hasLink && newArticleDetailsDisplay">
          The content is available under
          <a :href="copyrightLink" target="_blank" data-test-id="LicenseLink">
            <span>{{ label }}</span>
            <span class="d-sr-only">[opens in a new tab]</span>
            <v-icon small color="primary" aria-hidden="true">{{
              mdiOpenInNew
            }}</v-icon>
          </a>
        </span>

        <span v-else>
          The content is available as read only. Users of the site can access,
          read and download the content for private and personal use only, in
          accordance with the Site's
          <router-link
            :to="`/engage/${partner}/legal-information?show=terms-of-use`"
            target="_blank"
            data-test-id="LicenseLink"
          >
            <span>Terms of Use</span>
            <span class="d-sr-only">[opens in a new tab]</span>
            <v-icon small color="primary" aria-hidden="true">{{
              mdiOpenInNew
            }}</v-icon>
          </router-link>
          . No reuse permitted.
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mdiOpenInNew } from "@mdi/js";
import mapFlags from "~/plugins/utilities/mapFlags";

export default {
  name: "LicenseInformation",
  props: {
    article: { type: Object, required: true },
  },
  data: () => {
    return {
      showInFull: false,
      // Icons
      mdiOpenInNew,
    };
  },
  computed: {
    partner() {
      return this.$config.partner;
    },
    label() {
      return this.article.license.label;
    },
    copyrightWithLink() {
      return `The content is available under ${this.label}`;
    },
    hasLink() {
      return !!this.article.license.url;
    },
    copyrightLink() {
      return this.article.license.url;
    },
    upperCaseLabel() {
      return this.label.toUpperCase();
    },
    showLabel() {
      return this.article.license.name === "READ-ONLY";
    },
    showCC() {
      return (
        this.upperCaseLabel.includes("CC") &&
        !this.upperCaseLabel.includes("CC 0")
      );
    },
    showBY() {
      return this.upperCaseLabel.includes("BY");
    },
    showNC() {
      return this.upperCaseLabel.includes("NC");
    },
    showSA() {
      return this.upperCaseLabel.includes("SA");
    },
    showND() {
      return this.upperCaseLabel.includes("ND");
    },
    showZERO() {
      return this.upperCaseLabel.includes("CC 0");
    },
    ...mapFlags(["newArticleDetailsDisplay"]),
  },
  methods: {
    showLicenseLabel(license) {
      if (license === "CC") {
        return (
          this.upperCaseLabel.includes("CC") &&
          !this.upperCaseLabel.includes("CC 0")
        );
      }
      return this.upperCaseLabel.includes(license);
    },
  },
};
</script>

<style lang="scss" scoped>
.license-information {
  &__image {
    height: 32px;
    width: 32px;
  }
}

.license-icon {
  &__container {
    width: 50px;
  }

  &__image {
    height: 40px;
    width: 40px;
  }
  &__label {
    font-size: 12px;
    line-height: 17px;
    font-weight: 500;
  }
}
</style>
